<template>
  <header
    class="page-hero"
    :class="{ 'page-hero--compact': props.compact }"
    :style="{ backgroundImage: imgBg }"
    role="banner"
  >
    <video
      v-if="hasVideoBackground"
      class="page-hero__video"
      :src="videoBg || undefined"
      disablepictureinpicture
      playsinline
      autoplay
      muted
      loop
    />
    <div class="page-hero__background">
      <slot name="background" />
    </div>

    <div class="page-hero__content" v-intersect>
      <div v-if="slots.pretitle" class="page-hero__pretitle">
        <slot name="pretitle" />
      </div>

      <h1 class="page-hero__title"><slot name="title" /></h1>

      <div v-if="slots.widget" class="page-hero__widget">
        <slot name="widget" />
      </div>

      <div v-if="slots.buttons" class="page-hero__buttons">
        <slot name="buttons" />
      </div>

      <div v-if="slots.footer" class="page-hero__footer">
        <slot name="footer" />
      </div>

      <Breadcrumbs
        v-if="props.breadcrumbs?.length"
        :items="props.breadcrumbs"
      />
    </div>
  </header>
</template>

<script lang="ts" setup>
  import type { Breadcrumb } from "~/lib/types";
  import type { MediaFragment } from "~/lib/api-types";
  import { getResponsiveImage } from "~/lib/helpers";

  const props = defineProps<{
    background?: MediaFragment;
    mobileBackground?: MediaFragment;
    breadcrumbs?: Breadcrumb[];
    compact?: boolean;
  }>();

  const slots = useSlots();
  const isMobil = ref<boolean>(false);

  onMounted(() => {
    watchMobil();
    window.addEventListener("resize", watchMobil);
  });
  onUnmounted(() => {
    window.removeEventListener("resize", watchMobil);
  });

  const watchMobil = () => {
    isMobil.value = window.innerWidth >= 768 ? false : true;
  };

  const hasVideoBackground = computed(() => {
    return isMobil.value
      ? props.mobileBackground?.mime_type?.startsWith("video")
      : props.background?.mime_type?.startsWith("video");
  });

  const imgBg = computed(() => {
    const source = isMobil.value ? props.mobileBackground : props.background;
    const url = !hasVideoBackground.value
      ? `url(${getResponsiveImage(source, "3840")})`
      : "none";
    return url;
  });
  const videoBg = computed(() => {
    const url =
      hasVideoBackground.value && isMobil.value
        ? props.mobileBackground?.original_url
        : props.background?.original_url;
    return url;
  });
</script>

<style lang="scss">
  .page-hero {
    display: flex;
    min-height: _rem(760px);
    padding: 0 var(--page-margin);
    background-size: cover;
    background-position: center;

    @include breakpoint(small down) {
      min-height: 100vh;
    }

    &--compact {
      max-height: _rem(500px);
    }

    &::before {
      content: "";
      @include absolute-cover;
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0) 55.45%,
        rgba(0, 0, 0, 0.48) 86.29%
      );
    }

    &__background {
      @include absolute-cover;
      display: flex;
      overflow: hidden;
      align-items: center;
      justify-content: center;

      &:empty {
        background: linear-gradient(
          to top,
          rgba(0, 0, 0, 0.25),
          transparent 50%
        );

        &::after {
          content: "";
          @include absolute-cover;
          background: linear-gradient(
            to top,
            rgba(0, 0, 0, 0.6),
            transparent 50%
          );
          mix-blend-mode: overlay;
        }
      }
    }

    &__video {
      pointer-events: none;
      @include absolute-cover;
      object-fit: cover;
      object-position: center;
    }

    &__content {
      width: 100%;
      max-width: var(--page-width);
      color: var(--color-white);
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      padding-top: _rem(250px);
      padding-bottom: _rem(144px);
      margin: 0 auto;
      position: relative;

      @media (max-height: 760px), (max-width: 767px) {
        padding-bottom: _rem(96px);
      }

      @include breakpoint(small down) {
        padding-top: _rem(200px);
      }

      > * {
        width: fit-content;
        z-index: 1;
      }
    }

    &__pretitle {
      @include caption-font;
      text-transform: uppercase;
      padding-left: _rem(2px);
      font-size: _rem(14px);
    }

    &__title {
      margin-top: _rem(16px);
      max-width: _rem(660px);
      text-shadow: 0 2px 0.2em rgba(0, 0, 0, 0.15);
      @include animation-slide-fade-in($delay: 150ms);
    }
    &__title + &__buttons {
      margin-top: _rem(20px);
    }

    &__widget {
      color: var(--color-dark);
      margin-top: _rem(34px);
      max-width: _rem(375px);
      padding: _rem(8px) _rem(16px);
      background-color: var(--color-white);
      border-radius: var(--box-radius);
      @include animation-slide-fade-in($delay: 300ms);
    }
    &__widget + &__buttons {
      margin-top: _rem(34px);
    }

    &__buttons {
      @include animation-slide-fade-in($delay: 300ms);
      display: flex;
      flex-wrap: wrap;
      gap: _rem(16px);
    }

    &__footer {
      @include animation-slide-fade-in($delay: 300ms);
      margin-top: _rem(34px);
    }

    .breadcrumbs {
      position: absolute;
      bottom: _rem(24px);
    }
  }
</style>
